import { useQueryClient } from '@tanstack/react-query';
import type { FormEvent } from 'react';

import { useCompany, useCompanyId } from './useCompany';
import { companies } from '../../api/endpoints';
import { Button } from '../../components/Button';
import { Card, PageHeader } from '../../components/Surface';
import { ErrorBanner } from '../../components/ErrorBanner';
import { Field } from '../../components/Field';
import { fieldStyles } from '../../components/styles';
import { SkeletonRows } from '../../components/Skeleton';
import { toast } from '../../components/Toast';
import { useT } from '../../i18n/provider';
import { cn } from '../../lib/cn';
import { useSubmit } from '../auth/useSubmit';

/**
 * `13 §2`: nombre, misión, idioma y zona horaria.
 *
 * Ojo con «idioma de trabajo»: es el idioma en el que **escriben los
 * empleados IA** (`03 §159`), no el de esta interfaz —ese vive en el usuario y
 * se elige en la pantalla de cuenta—. La explicación del campo lo dice, porque
 * los dos ajustes se llaman parecido y están a dos clics uno del otro.
 */
export function GeneralSettingsScreen() {
  const t = useT();
  const id = useCompanyId();
  const company = useCompany();
  const queryClient = useQueryClient();
  const { busy, error, fields, submit } = useSubmit<void>();

  if (company.isPending) {
    return <SkeletonRows rows={2} />;
  }

  if (company.error) {
    return <ErrorBanner error={company.error} onRetry={() => void company.refetch()} />;
  }

  const settings = company.data?.settings as
    | { working_language?: string; timezone?: string; budget_limits_enforced?: boolean }
    | undefined;

  // Absent reads as enforced, the same way the backend reads it: a settings
  // document missing the key is a company from before the switch existed, and
  // it has its brakes on.
  const limitsEnforced = settings?.budget_limits_enforced !== false;

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);

    void submit(async () => {
      await companies.update(id, {
        name: String(form.get('name') ?? ''),
        industry: String(form.get('industry') ?? '') || null,
        mission: String(form.get('mission') ?? '') || null,
      });

      // A separate call because it is a separate endpoint with separate
      // validation: `PATCH /settings` merges into a schema-checked document,
      // and folding the two together would make one 422 unattributable.
      await companies.updateSettings(id, {
        working_language: String(form.get('working_language') ?? ''),
        timezone: String(form.get('timezone') ?? ''),
        budget_limits_enforced: form.get('budget_limits_enforced') === 'on',
      });

      await queryClient.invalidateQueries({ queryKey: ['company', id] });
      toast.success(t('settings.account.saved'));
    });
  };

  return (
    <form onSubmit={onSubmit} className="max-w-lg space-y-4">
      <PageHeader title={t('company.tabs.general')} description={t('generalSettings.intro')} />

      {error ? <ErrorBanner error={error} /> : null}

      <Field label={t('companyWizard.name')} name="name" defaultValue={company.data?.name} required error={fields.name} />
      <Field
        label={t('companyWizard.industry')}
        name="industry"
        defaultValue={company.data?.industry ?? ''}
        error={fields.industry}
      />

      <div className="space-y-1.5">
        <label htmlFor="mission" className="block text-sm font-semibold text-ink">
          {t('companyWizard.mission')}
        </label>
        <textarea
          id="mission"
          name="mission"
          rows={3}
          defaultValue={company.data?.mission ?? ''}
          className={cn(fieldStyles, 'resize-y border-line')}
        />
        <p className="text-xs leading-relaxed text-ink-soft">{t('companyWizard.missionHint')}</p>
      </div>

      <Field
        label={t('companySettings.workingLanguage')}
        name="working_language"
        defaultValue={settings?.working_language ?? 'es'}
        hint={t('companySettings.workingLanguageHint')}
        error={fields.working_language}
      />
      <Field
        label={t('companySettings.timezone')}
        name="timezone"
        defaultValue={settings?.timezone ?? 'Europe/Madrid'}
        hint={t('companySettings.timezoneHint')}
        error={fields.timezone}
      />

      {/* El interruptor de los topes de gasto. Casilla y no desplegable
          porque solo tiene dos estados, y con el aviso al lado en vez de en un
          diálogo: desmarcarla no rompe nada hoy, se nota al final de mes. */}
      <Card tone="muted" size="sm" className={limitsEnforced ? undefined : 'border-warning-line bg-warning-pale/40'}>
        <label className="flex cursor-pointer items-start gap-2.5">
          <input
            type="checkbox"
            name="budget_limits_enforced"
            defaultChecked={limitsEnforced}
            className="mt-0.5 size-4 accent-primary"
          />
          <span>
            <span className="text-sm font-semibold text-ink">{t('companySettings.budgetLimits')}</span>
            <span className="mt-1 block text-xs leading-relaxed text-ink-soft">
              {t('companySettings.budgetLimitsHint')}
            </span>
          </span>
        </label>
      </Card>

      {/* The slug is shown and not editable: it is already part of repository
          names on GitHub, so invariant I-16 fixes it at creation. */}
      <Card tone="muted" size="sm">
        <p className="text-xs font-semibold text-ink-soft">{t('companyWizard.slug')}</p>
        <p className="font-mono text-sm text-ink">{company.data?.slug}</p>
        <p className="mt-1 text-xs text-ink-faint">{t('companySettings.slugFixed')}</p>
      </Card>

      <Button type="submit" busy={busy}>
        {t('common.saveChanges')}
      </Button>
    </form>
  );
}
